// Nearest hospital / medical centre — one block shared by the Site folder
// (step 5), the New site form and the site Edit form. James, 27 Sep 2026:
// "add the same worker to the site add library so we can capture the
// hospital details at the start and on future sites". All three write the
// same sites.nearest_* columns, so filling it anywhere fills it everywhere.
//
// The lookup is the gateway's POST /hse/site-lookup (OpenStreetMap — no key):
// what's at the GPS, where the address geocodes to, hospitals in rings up to
// 400 km (emergency departments first), doctors / clinics within 60 km.
// Nothing is saved until the parent form saves.
import { useEffect, useState } from "react";
import { Hospital, Search } from "lucide-react";
import { apiFetch } from "@/lib/supabase";
import { Button, Input } from "@/components/ui";

/** James: ">100 km is a risk to be seen" — computed, never stored. */
export const REMOTE_KM = 100;
export function isRemoteKm(km: number | string | null | undefined): boolean {
  return km != null && String(km).trim() !== "" && Number(km) > REMOTE_KM;
}

/** All strings — the form owns parsing on save. */
export interface MedicalValues {
  nearest_hospital_name: string;
  nearest_hospital_address: string;
  nearest_hospital_phone: string;
  nearest_hospital_distance_km: string;
  nearest_medical_centre_name: string;
  nearest_medical_centre_address: string;
  nearest_medical_centre_phone: string;
  nearest_medical_centre_distance_km: string;
}

export const EMPTY_MEDICAL: MedicalValues = {
  nearest_hospital_name: "", nearest_hospital_address: "", nearest_hospital_phone: "", nearest_hospital_distance_km: "",
  nearest_medical_centre_name: "", nearest_medical_centre_address: "", nearest_medical_centre_phone: "", nearest_medical_centre_distance_km: "",
};

/** From a sites row (any shape) to the string form. */
export function medicalFromSite(s: any): MedicalValues {
  const str = (v: any) => (v == null ? "" : String(v));
  return {
    nearest_hospital_name: str(s?.nearest_hospital_name ?? s?.nearest_hospital),
    nearest_hospital_address: str(s?.nearest_hospital_address),
    nearest_hospital_phone: str(s?.nearest_hospital_phone),
    nearest_hospital_distance_km: str(s?.nearest_hospital_distance_km),
    nearest_medical_centre_name: str(s?.nearest_medical_centre_name),
    nearest_medical_centre_address: str(s?.nearest_medical_centre_address),
    nearest_medical_centre_phone: str(s?.nearest_medical_centre_phone),
    nearest_medical_centre_distance_km: str(s?.nearest_medical_centre_distance_km),
  };
}

/** The sites columns to write. `nearest_hospital` (the older start-card column) is kept in step with the name. */
export function medicalToPayload(m: MedicalValues) {
  const t = (v: string) => v.trim() || null;
  const n = (v: string) => (v.trim() === "" || isNaN(Number(v)) ? null : Number(v));
  return {
    nearest_hospital_name: t(m.nearest_hospital_name), nearest_hospital: t(m.nearest_hospital_name),
    nearest_hospital_address: t(m.nearest_hospital_address), nearest_hospital_phone: t(m.nearest_hospital_phone),
    nearest_hospital_distance_km: n(m.nearest_hospital_distance_km),
    nearest_medical_centre_name: t(m.nearest_medical_centre_name), nearest_medical_centre_address: t(m.nearest_medical_centre_address),
    nearest_medical_centre_phone: t(m.nearest_medical_centre_phone), nearest_medical_centre_distance_km: n(m.nearest_medical_centre_distance_km),
  };
}

export function MedicalLookup({ lat, lng, address, value, onChange, canEdit = true, onLookup }: {
  lat: number | string | null | undefined;
  lng: number | string | null | undefined;
  address?: string;
  value: MedicalValues;
  onChange: (next: MedicalValues) => void;
  canEdit?: boolean;
  /** the raw lookup result, if the parent wants the address check etc. */
  onLookup?: (result: any) => void;
}) {
  const [lookup, setLookup] = useState<any | null>(null);
  const [finding, setFinding] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!finding) { setSecs(0); return; }
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [finding]);

  const set = (patch: Partial<MedicalValues>) => onChange({ ...value, ...patch });
  const latN = lat == null || String(lat).trim() === "" ? null : Number(lat);
  const lngN = lng == null || String(lng).trim() === "" ? null : Number(lng);
  const hasGps = latN != null && lngN != null && !isNaN(latN) && !isNaN(lngN);
  const remote = isRemoteKm(value.nearest_hospital_distance_km) || (lookup?.remote === true && value.nearest_hospital_distance_km.trim() === "");

  async function find() {
    if (!hasGps) { setErr("Enter the site's latitude and longitude first — the search runs from the GPS."); return; }
    setFinding(true); setErr(null);
    try {
      const r = await apiFetch("/hse/site-lookup", { method: "POST", body: JSON.stringify({ lat: latN, lng: lngN, address: address?.trim() || undefined }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? `lookup ${r.status}`);
      setLookup(j); onLookup?.(j);
      // nothing picked yet? pre-fill with the closest of each so a one-click Save works
      if (!value.nearest_hospital_name.trim() && j.hospitals?.[0]) pickHospital(j.hospitals[0], j.clinics?.[0]);
      else if (!value.nearest_medical_centre_name.trim() && j.clinics?.[0]) pickClinic(j.clinics[0]);
    } catch (e: any) { setErr(e.message); } finally { setFinding(false); }
  }
  function pickHospital(h: any, alsoClinic?: any) {
    const next: Partial<MedicalValues> = { nearest_hospital_name: h.name, nearest_hospital_address: h.address ?? "", nearest_hospital_phone: h.phone ?? "", nearest_hospital_distance_km: String(h.distance_km) };
    if (alsoClinic && !value.nearest_medical_centre_name.trim()) Object.assign(next, { nearest_medical_centre_name: alsoClinic.name, nearest_medical_centre_address: alsoClinic.address ?? "", nearest_medical_centre_phone: alsoClinic.phone ?? "", nearest_medical_centre_distance_km: String(alsoClinic.distance_km) });
    set(next);
  }
  function pickClinic(h: any) {
    set({ nearest_medical_centre_name: h.name, nearest_medical_centre_address: h.address ?? "", nearest_medical_centre_phone: h.phone ?? "", nearest_medical_centre_distance_km: String(h.distance_km) });
  }

  const Row = ({ h, onPick, showEd }: { h: any; onPick: () => void; showEd?: boolean }) => (
    <button type="button" onClick={onPick} disabled={!canEdit} className="w-full text-left rounded border border-line px-2 py-1.5 hover:border-accent/50 flex items-center gap-2 disabled:opacity-60">
      <span className="font-mono text-text-muted w-[52px] shrink-0">{h.distance_km} km</span>
      <span className="text-text flex-1 min-w-0 truncate">{h.name}{h.address ? <span className="text-text-muted"> · {h.address}</span> : null}</span>
      {showEd && h.emergency === true && <span className="text-[10px] font-bold text-danger">ED</span>}
      {h.phone && <span className="font-mono text-text-muted">{h.phone}</span>}
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><Hospital size={12}/> Nearest hospital (emergency)</div>
      <Input value={value.nearest_hospital_name} onChange={e => set({ nearest_hospital_name: e.target.value })} placeholder="Hospital with an emergency department" disabled={!canEdit}/>
      <Input value={value.nearest_hospital_address} onChange={e => set({ nearest_hospital_address: e.target.value })} placeholder="Address" disabled={!canEdit}/>
      <div className="grid grid-cols-[1fr_90px] gap-2">
        <Input value={value.nearest_hospital_phone} onChange={e => set({ nearest_hospital_phone: e.target.value })} placeholder="Phone" disabled={!canEdit}/>
        <Input value={value.nearest_hospital_distance_km} onChange={e => set({ nearest_hospital_distance_km: e.target.value })} placeholder="km" mono disabled={!canEdit}/>
      </div>
      {remote && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12px] text-danger">
          <b>Remote site</b> — nearest hospital is {value.nearest_hospital_distance_km.trim() ? `${value.nearest_hospital_distance_km} km` : `more than ${REMOTE_KM} km`} away. Plan for it: first aider and kit on site, comms checked, and the crew told before they go. The Site folder flags this to them.
        </div>
      )}
      <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5 pt-2"><Hospital size={12}/> Nearest doctor / medical centre (minor first aid)</div>
      <Input value={value.nearest_medical_centre_name} onChange={e => set({ nearest_medical_centre_name: e.target.value })} placeholder="GP / medical centre — optional" disabled={!canEdit}/>
      <Input value={value.nearest_medical_centre_address} onChange={e => set({ nearest_medical_centre_address: e.target.value })} placeholder="Address" disabled={!canEdit}/>
      <div className="grid grid-cols-[1fr_90px] gap-2">
        <Input value={value.nearest_medical_centre_phone} onChange={e => set({ nearest_medical_centre_phone: e.target.value })} placeholder="Phone" disabled={!canEdit}/>
        <Input value={value.nearest_medical_centre_distance_km} onChange={e => set({ nearest_medical_centre_distance_km: e.target.value })} placeholder="km" mono disabled={!canEdit}/>
      </div>
      <div className="flex items-center gap-3 pt-1 flex-wrap">
        <Button size="sm" variant="secondary" icon={Search} onClick={find} disabled={finding || !canEdit}>{finding ? `Searching… ${secs}s` : "Find nearest & check the address"}</Button>
        {!hasGps && <span className="text-[11px] text-warn">Needs the site's GPS.</span>}
        {finding && <span className="text-[11px] text-text-muted">Up to 400 km for a hospital, 60 km for a doctor — usually 5–15 s, never more than 30.</span>}
      </div>
      {err && <div className="text-[12px] text-danger">{err}</div>}
      {lookup && (
        <div className="rounded-md bg-ink border border-line p-3 text-[12px] space-y-2">
          {lookup.reverse && <div><span className="text-text-muted">At the GPS: </span><span className="text-text">{lookup.reverse.display}</span></div>}
          {lookup.mismatch_km != null && (
            <div className={lookup.mismatch_km > 2 ? "text-danger" : "text-ok"}>
              {lookup.mismatch_km > 2 ? `⚠ The recorded address geocodes ${lookup.mismatch_km} km from the GPS — one of them is wrong.` : `✓ Address and GPS agree (${lookup.mismatch_km < 1 ? `${Math.round(lookup.mismatch_km * 1000)} m` : `${lookup.mismatch_km} km`} apart).`}
            </div>
          )}
          {lookup.hospitals?.length > 0 ? (
            <div className="space-y-1">
              <div className="text-text-muted">Closest hospitals within {lookup.search_radius_km ?? 400} km (straight line — road is further){lookup.nearest_ed_km != null ? ` · nearest ED ${lookup.nearest_ed_km} km` : " · no ED tagged"}:</div>
              {lookup.hospitals.map((h: any, i: number) => <Row key={i} h={h} onPick={() => pickHospital(h)} showEd/>)}
              <div className="text-[11px] text-text-muted">Tap one to fill the fields, then check the phone on their website — OpenStreetMap doesn't always carry it.</div>
            </div>
          ) : lookup.search_failed
            ? <div className="text-warn">The map servers didn't answer this time (they rate-limit) — wait a minute and press it again, or type it in.</div>
            : <div className="text-danger">No hospital tagged within 400 km — type it in, and treat this site as remote.</div>}
          {lookup.clinics?.length > 0 && (
            <div className="space-y-1 pt-1">
              <div className="text-text-muted">Closest doctors / medical centres (within 60 km):</div>
              {lookup.clinics.map((h: any, i: number) => <Row key={i} h={h} onPick={() => pickClinic(h)}/>)}
            </div>
          )}
          {lookup.warnings?.length > 0 && <div className="text-[11px] text-text-muted">{lookup.warnings.join(" · ")}</div>}
          {lookup.took_s != null && <div className="text-[11px] text-text-muted">Search took {lookup.took_s} s{lookup.hospitals_source ? ` · source ${lookup.hospitals_source}` : ""}.</div>}
        </div>
      )}
    </div>
  );
}
